import { Box, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, LinearProgress, OutlinedInput, Paper, Switch, TextField, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDeleteEtapaMutation, useGetEtapasQuery, useReativarEtapaMutation, useUpdateEtapaMutation } from "shared/features/api/etapa/etapaSlice"
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from '@mui/icons-material/Restore';
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Search } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { DeleteModal } from "components/DeleteModal";
import { toast } from "react-toastify";

export const EtapaList: React.FC = () => {
    const [ativo, setAtivo] = useState<boolean>(true);
    const [open, setOpen] = useState(false);
    const isBelow1705 = useMediaQuery("(max-width:1705px)");
    const {data: etapas, isLoading: carregandoEtapas} = useGetEtapasQuery(ativo);
    const [idEtapaRow, setIdEtapaRow] = useState<number>();
    const [deleteEtapa] = useDeleteEtapaMutation();
    const [reativarEtapa] = useReativarEtapaMutation();
    const [updateEtapa] = useUpdateEtapaMutation();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const [nomeInput, setNomeInput] = useState<string>("");

    const etapasFiltradas = useMemo(() => {
        return search.length > 0
        ? etapas?.filter((etapa) =>
            etapa.nome.toLowerCase().includes(search.toLowerCase())
            )
        : etapas;
    }, [etapas, search]);

    useEffect(() => {
        if (isEditing && idEtapaRow !== undefined) {
            const etapaSelecionada = etapasFiltradas?.find(etapa => etapa.idEtapa === idEtapaRow);
                if (etapaSelecionada) {
                    setNomeInput(etapaSelecionada.nome);
                }
        }
    }, [isEditing, idEtapaRow, etapasFiltradas]);

    const handleConfirmDelete = (id: number) => {
        setOpen(true);
        setIdEtapaRow(id);
    }

    const handleDelete = async (id: number) => {
        await toast.promise(
            deleteEtapa(id).unwrap(),
            {
                pending: "Carregando...",
                success: `Etapa desativada com sucesso!`,
                error: {
                    render({ data }) {
                    const response = data as {
                        data?: {
                        errors?: string[];
                        message?: string;
                        };
                    };

                    const errorMessage =
                        response.data?.errors?.[0] ||
                        response.data?.message ||
                        `Erro ao desativar etapa.`;
                    return errorMessage;
                    },
                },
            }
        )
    }

    const handleRestore = async (id:number) => {
        await toast.promise(
            reativarEtapa(id).unwrap(),
            {
                pending: "Carregando...",
                success: `Etapa reativada com sucesso!`,
                error: {
                    render({ data }) {
                    const response = data as {
                        data?: {
                        errors?: string[];
                        message?: string;
                        };
                    };

                    const errorMessage =
                        response.data?.errors?.[0] ||
                        response.data?.message ||
                        `Erro ao reativar etapa.`;
                    return errorMessage;
                    },
                },
            }
        )
    }

    const handleUpdate = async (id: number, nome: string) => {
        const payloadUpdateEtapa = {
            idEtapa: id,
            nome: nome
        }

        await toast.promise(
            updateEtapa(payloadUpdateEtapa).unwrap(),
            {
                pending: "Carregando...",
                success: `Etapa atualizada com sucesso!`,
                error: {
                    render({ data }) {
                    const response = data as {
                        data?: {
                        errors?: string[];
                        message?: string;
                        };
                    };

                    const errorMessage =
                        response.data?.errors?.[0] ||
                        response.data?.message ||
                        `Erro ao atualizar etapa.`;
                    return errorMessage;
                    },
                },
            }
            
        )
        handleCloseEdition();
    }

    const handleCloseEdition = () => {
        setIsEditing(false);
        setIdEtapaRow(undefined);
        setNomeInput("");
    }

    const columns = [
        {
            field: "nome",
            headerName: "Etapa",
            flex: 2,
            renderCell: (params: any) => {
                const nomeEtapa = params.row.nome;
                const isCurrentRowEditing = isEditing && idEtapaRow === params.row.id;

                return isCurrentRowEditing ? (
                    <TextField
                    size="small"
                    value={nomeInput}
                    onChange={(e) => setNomeInput(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    />
                ) : (
                    <Typography>{nomeEtapa}</Typography>
                );
            }
        },
        {
            field: "acoes",
            headerName: "Ações",
            flex: 2,
            sortabale: false,
            disableColumnMenu: true,
            renderCell: (params: any) => {
                const idEtapa = params.row.id;
                const isCurrentRowEditing = isEditing && idEtapaRow === idEtapa;
                return (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                        width="100%"
                    >
                        {ativo ? (
                        <>
                            {
                                isCurrentRowEditing ? (
                                    <>
                                        <IconButton
                                            onClick={() => handleUpdate(idEtapa, nomeInput)}
                                        >
                                            <CheckIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => {
                                                handleCloseEdition();
                                            }}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        <IconButton
                                            onClick={() => {
                                                setIsEditing(true);
                                                setIdEtapaRow(idEtapa);
                                            }} 
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleConfirmDelete(idEtapa)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </>
                                )
                            }
                            
                        </>
                        ) : (
                        <IconButton
                            onClick={() => handleRestore(idEtapa)}
                        >
                            <RestoreIcon />
                        </IconButton>
                        )}
                    </Box>
                )
            }
        }
    ];

    const rows = () => {
        return etapasFiltradas?.map((etapa) => {
            return {
                id: etapa.idEtapa,
                nome: etapa.nome
            };
    });
  };

    return (
        <>
            <Grid
                item
                md={12}
                lg={isBelow1705 ? 12: 7.5}
                sx={{
                    borderRadius: "5px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    height: "fit-content",
                }}
            >
                <Paper sx={{ padding: "16px" }}>
                    <Typography fontSize={20} fontWeight={600}>
                        Etapas
                    </Typography>
                    <Grid item xs={12} display="flex" alignItems="center" gap={2} mt={1}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel htmlFor="search-edição">Pesquisar por etapa</InputLabel>
                            <OutlinedInput
                                id="search-edição"
                                label="Pesquisar por edição"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                endAdornment={
                                <InputAdornment position="end">
                                    <IconButton edge="end" type="submit">
                                    <Search color="primary" />
                                    </IconButton>
                                </InputAdornment>
                                }
                            />
                        </FormControl>
            
                        <FormControlLabel
                            control={
                                <Switch
                                    value={ativo}
                                    checked={ativo}
                                    onChange={() => setAtivo(!ativo)}
                                />
                            }
                            label="Ativas"
                            labelPlacement="end"
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ width: "100%", mt: 2 }}>
                        {carregandoEtapas ? (
                            <LinearProgress />
                        ) : (
                        <DataGrid
                            rows={rows() || []}
                            columns={columns}
                            pageSize={20}
                            rowsPerPageOptions={[5]}
                            hideFooter
                            sx={{
                                height: 280,
                                boxShadow: 2,
                                "& .MuiDataGrid-cell": {
                                    fontSize: "14px",
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    fontSize: "16px",
                                    whiteSpace: "nowrap",
                                },
                                "& .MuiDataGrid-row": {
                                    maxHeight: "40px",
                                },
                            }}
                        />
                    )}
                    </Grid>
                </Paper> 
            </Grid>
            <DeleteModal 
                open={open} 
                setOpen={setOpen} 
                title={"Confirmar desativação"} 
                context={"Realmente deseja desativar a edição?"} 
                deleteFunction={() => handleDelete(idEtapaRow!)}
            />
        </>
    )
}